import { ALL_TOOLS } from './toolsCatalog';
import { SIMULATOR_CATALOG } from './simulationCatalog';
import { howItWorksApi } from './api';

// How-it-works topics are now DB-backed (admin-editable), not a static catalog file,
// so this cache is populated asynchronously on load rather than being available
// synchronously like ALL_TOOLS/SIMULATOR_CATALOG. Search results for this type are
// simply empty until the first fetch resolves (typically well before a user searches).
let howItWorksTopicsCache = [];
howItWorksApi.getTopics().then((topics) => { howItWorksTopicsCache = topics; }).catch(() => {});

function matches(text, q) {
  return !!text && text.toLowerCase().includes(q);
}

function matchCatalog(catalog, query, fields = {}) {
  const { title = 'title', description = 'description', category = 'category' } = fields;
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return catalog.filter(
    (item) => matches(item[title], q) || matches(item[description], q) || matches(item[category], q)
  );
}

// Central registry of every searchable content type on the site. NavbarSearch and
// SearchPage render entirely from this list (labels, groups, tabs) — to add a new
// content type to search, add one entry here instead of touching either component.
//
// source:
//   'backend' — matched entirely server-side (GET /api/search, see
//               backend/core/search_providers.py); group comes straight from the
//               API response for this `type`.
//   'client'  — matched entirely against a frontend catalog file, for content that
//               has no database model yet.
//   'both'    — merges a backend group (e.g. DB-backed User Guides) with client-side
//               catalog matches (the tool/simulator pages themselves) into one group.
//   'soon'    — section isn't live yet; only ever surfaces as a disabled "SOON" row
//               when the query text matches the section's own label.
export const SEARCH_TYPES = [
  { type: 'articles', label: 'Articles', source: 'backend' },
  {
    type: 'tools',
    label: 'Tools',
    source: 'both',
    matchCatalog: (query) =>
      matchCatalog(ALL_TOOLS, query).map((tool) => ({
        id: tool.to,
        title: tool.title,
        excerpt: tool.description,
        category: tool.category,
        url: tool.to,
      })),
  },
  {
    type: 'simulation',
    label: 'Simulation',
    source: 'both',
    matchCatalog: (query) =>
      matchCatalog(SIMULATOR_CATALOG, query).map((sim) => ({
        id: sim.to,
        title: sim.title,
        excerpt: sim.description,
        category: 'Simulator',
        url: sim.to,
      })),
  },
  {
    type: 'how-it-works',
    label: 'How Does It Work?',
    source: 'client',
    matchCatalog: (query) =>
      matchCatalog(howItWorksTopicsCache, query, { description: 'short_description' }).map((topic) => ({
        id: topic.topic_id,
        title: topic.title,
        excerpt: topic.short_description,
        category: topic.category,
        url: `/how-it-works/${topic.topic_id}`,
      })),
  },
  { type: 'community', label: 'Community', source: 'backend' },
  { type: 'courses', label: 'Courses', source: 'soon', url: '/courses' },
];

function cap(items, limit) {
  return limit ? items.slice(0, limit) : items;
}

// Combines backend groups (from GET /api/search) with client-side catalog matches
// into the unified, capped group list both the navbar dropdown and the full results
// page render, driven purely by SEARCH_TYPES above. `limit` caps how many items per
// group are returned; `count` always reflects the true total match count (used for
// "View all results" links and the results-page tab counts).
export function buildUnifiedGroups(query, backendGroups = [], limit = 5) {
  const byType = Object.fromEntries(backendGroups.map((g) => [g.type, g]));
  const q = query.trim().toLowerCase();

  return SEARCH_TYPES.flatMap((entry) => {
    if (entry.source === 'soon') {
      if (!q || !matches(entry.label, q)) return [];
      return [{ type: entry.type, label: entry.label, count: 0, items: [], comingSoon: true, url: entry.url }];
    }

    const backend = byType[entry.type] || { count: 0, items: [] };

    if (entry.source === 'backend') {
      return [{ type: entry.type, label: entry.label, count: backend.count, items: cap(backend.items, limit) }];
    }

    const catalogItems = entry.matchCatalog(query);

    if (entry.source === 'client') {
      return [{ type: entry.type, label: entry.label, count: catalogItems.length, items: cap(catalogItems, limit) }];
    }

    // 'both': the tool/simulator pages themselves rank above their User Guides.
    return [
      {
        type: entry.type,
        label: entry.label,
        count: catalogItems.length + backend.count,
        items: cap([...catalogItems, ...backend.items], limit),
      },
    ];
  });
}
